import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/contexts/CartContext";
import { CartBar } from "@/components/workbooks/CartBar";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Analytics } from "@/components/Analytics";
import { CookieConsent } from "@/components/CookieConsent";
import { RedirectLegacyArticle, RedirectLegacyCategory, RedirectLegacyHome } from "./components/RedirectLegacyRoutes";

// Eagerly load Home for fastest initial paint
import Home from "./pages/Home";

// Lazy-load all other routes
const Category = lazy(() => import("./pages/Category"));
const Article = lazy(() => import("./pages/Article"));
const Search = lazy(() => import("./pages/Search"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const Glossary = lazy(() => import("./pages/Glossary"));
const GlossaryTermPage = lazy(() => import("./pages/GlossaryTermPage"));
const Tools = lazy(() => import("./pages/Tools"));
const Workbooks = lazy(() => import("./pages/Workbooks"));
const CostIndex = lazy(() => import("./pages/CostIndex"));
const FieldNotes = lazy(() => import("./pages/FieldNotes"));
const PurchaseSuccess = lazy(() => import("./pages/PurchaseSuccess"));
const Library = lazy(() => import("./pages/Library"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ArticleStatus = lazy(() => import("./pages/admin/ArticleStatus"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));


const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <CartProvider>
          <ScrollToTop />
          <Analytics />
          <CookieConsent />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/category/:categoryId" element={<Category />} />
              <Route path="/article/:slug" element={<Article />} />
              <Route path="/search" element={<Search />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/glossary/:slug" element={<GlossaryTermPage />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/workbooks" element={<Workbooks />} />
              <Route path="/cost-index" element={<CostIndex />} />
              <Route path="/field-notes" element={<FieldNotes />} />
              <Route path="/purchase-success" element={<PurchaseSuccess />} />
              <Route path="/library" element={<Library />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/admin/article-status" element={<ArticleStatus />} />
              <Route path="/article-status" element={<ArticleStatus />} />
              <Route path="/admin/orders" element={<AdminOrders />} />

              
              {/* Legacy language redirects */}
              <Route path="/:lang/article/:slug" element={<RedirectLegacyArticle />} />
              <Route path="/:lang/category/:categoryId" element={<RedirectLegacyCategory />} />
              <Route path="/:lang/*" element={<RedirectLegacyHome />} />
              
              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CartBar />
          </CartProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
